import { Action, ActionReducerMapBuilder, createSlice, PayloadAction, SliceCaseReducers } from "@reduxjs/toolkit";
import { CreateEventForm } from "models/createEventForm";
import { CreateEventValidationSchema } from "models/CreateEventValidationSchema";
import { RootState } from "store";
import { ValidationError } from "yup";
import { asyncMutations, mutationBuilder } from "./createEvent.mutations";

export interface Category {
  id: number;
  text: string;
  typeId: number;
}

export interface State {
  createEventLoading: boolean;
  isDirty: boolean;
  isValid: boolean;
  open: boolean;
  errors: Record<string, string>;
  formValues: CreateEventForm;
  categories: Category[];
}

interface Actions extends SliceCaseReducers<State> {
  setOpen: (state: State, action: PayloadAction<{ open: boolean }>) => State;
  validateForm: (state: State, action: Action) => State;
  setValue: <K extends Extract<keyof CreateEventForm, string>>(
    state: State,
    action: PayloadAction<{ field: K; value: CreateEventForm[K]; shouldValidate?: boolean }>
  ) => State;
  handleClose: (state: State, action: Action) => State;
  setCategories: (state: State, action: PayloadAction<Category[]>) => State;
}

interface Selectors {
  selectCreateEventOpen: (state: RootState) => boolean;
  selectCreateEventForm: (state: RootState) => CreateEventForm;
  selectCategories: (state: RootState) => Category[];
}

export const initialState: State = {
  createEventLoading: false,
  isValid: true,
  isDirty: false,
  open: false,
  errors: {},
  formValues: {
    allDayToggle: false,
    selectedCategory: null,
    description: "",
    eventStartDate: null,
    eventEndDate: null,
  },
  categories: [],
};

const actions: Actions = {
  setOpen: (state, { payload: { open } }) => {
    state.open = open;
    return state;
  },
  setValue: (state, { payload: { field, value, shouldValidate } }) => {
    if (field === "selectedCategory" && value) {
      state.formValues.selectedCategory = { id: value.id, text: value.text }; 
    } else {
      state.formValues[field] = value;
    }
  
    state.isDirty = true;

    if (!shouldValidate && state.isValid) return state;

    const validationSchema = CreateEventValidationSchema();
    const isValidated = validationSchema.isValidSync(state.formValues);
    if (!isValidated) {
      try {
        validationSchema.validateSync(state.formValues, { strict: true, abortEarly: false });
      } catch (e: any) {
        const { inner } = e as ValidationError;
        state.errors = inner.reduce((acc, error) => {
          if (error.path) acc[error.path] = error.message;
          return acc;
        }, {} as Record<string, string>);
      }
      state.isValid = false;
    } else {
      state.isValid = true;
      state.errors = {};
    }
    return state;
  },
  setCategories: (state, { payload }) => {
    state.categories = payload; 
    return state;
  },
  validateForm: (state) => {
    const validationSchema = CreateEventValidationSchema();
    const isValidated = validationSchema.isValidSync(state.formValues);
    if (!isValidated) {
      try {
        validationSchema.validateSync(state.formValues, { strict: true, abortEarly: false });
      } catch (e: any) {
        const { inner } = e as ValidationError;
        state.errors = inner.reduce((acc, error) => {
          if (error.path) acc[error.path] = error.message;
          return acc;
        }, {} as Record<string, string>);
      }
      state.isValid = false;
    } else {
      state.isValid = true;
      state.errors = {};
    }
    return state;
  },
  handleClose: (state) => {
    state.open = false;
    state.createEventLoading = false;
    state.isValid = true;
    state.isDirty = false;
    state.errors = {};
    state.formValues = initialState.formValues;
    state.categories = []; 
    return state;
  }
};

const selectors: Selectors = {
  selectCreateEventOpen: ({ createEvent }) => createEvent.open,
  selectCreateEventForm: ({ createEvent }) => createEvent.formValues,
  selectCategories: ({ createEvent }) => createEvent.categories, 
};


const storeBase = createSlice<State, Actions>({
  name: "createEvent",
  initialState,
  reducers: actions,
  extraReducers: (builder: ActionReducerMapBuilder<State>) => {
    mutationBuilder(builder); 
  },
});

export default storeBase.reducer;

export const { setOpen, setValue, validateForm, handleClose, setCategories } = storeBase.actions;

export const { createEvent } = asyncMutations; 

export const { selectCreateEventOpen, selectCreateEventForm } = selectors;
